import { Resource as a, API as h } from "@coveo/platform-client";
const r = class r extends a {
  /**
   * Retrieve throttling data for a specific organization within the specified time range.
   *
   * @param dateRange An object containing 'from' and 'to' date strings in ISO 8601 format.
   */
  getThrottlingData(t) {
    return this.api.get(
      this.buildPath(`${r.baseUrl}/searchapi/usage/throttling`, {
        from: t.from,
        to: t.to
      })
    );
  }
  /**
   * Retrieve total queries and total queries chart for a specific organization within the specified time range.
   *
   * @param dateRange An object containing 'from' and 'to' date strings in ISO 8601 format.
   */
  getTotalQueriesData(t) {
    return this.api.get(
      this.buildPath(`${r.baseUrl}/searchapi/query-pipelines/usage/total`, {
        from: t.from,
        to: t.to
      })
    );
  }
  /**
   * Retrieve average response times and average response times chart for a specific organization within the specified time range.
   *
   * @param dateRange An object containing 'from' and 'to' date strings in ISO 8601 format.
   */
  getAverageResponseTimesData(t) {
    return this.api.get(
      this.buildPath(
        `${r.baseUrl}/searchapi/query-pipelines/usage/performance/average`,
        {
          from: t.from,
          to: t.to
        }
      )
    );
  }
  /**
   * Retrieve the limits for a specific organization.
   *
   * @param limits An optional array of limit types to filter the results.
   */
  getLimits(t) {
    const e = this.buildPath(`${r.baseUrl}/platform/limits`);
    if (t && t.length > 0) {
      const s = t.map((o) => `limitKeyRequests=${o}`).join("&");
      return this.api.get(`${e}?${s}`);
    }
    return this.api.get(e);
  }
  /**
   * Retrieve total indexed items and chart for a specific organization using the Push API within the specified time range using either predefined time range options or custom from/to dates.
   * @param dateRange An object containing 'from' and 'to' date strings in ISO 8601 format.
   */
  getTotalIndexedItems(t) {
    return this.api.get(
      this.buildPath(`${r.baseUrl}/index/items/total`, {
        from: t.from,
        to: t.to
      })
    );
  }
  /**
   * Retrieve total calls and chart for a specific organization using the Stream API within the specified time range using either predefined time range options or custom from/to dates.
   * @param dateRange An object containing 'from' and 'to' date strings in ISO 8601 format.
   */
  getStreamAPITotalRequests(t) {
    return this.api.get(
      this.buildPath(`${r.baseUrl}/streamapi/usage/total`, {
        from: t.from,
        to: t.to
      })
    );
  }
  /**
   * Retrieve total loads and chart for a specific organization using the Stream API within the specified time range using either predefined time range options or custom from/to dates.
   * @param dateRange An object containing 'from' and 'to' date strings in ISO 8601 format.
   */
  getStreamAPILoads(t) {
    return this.api.get(
      this.buildPath(`${r.baseUrl}/streamapi/usage/loads`, {
        from: t.from,
        to: t.to
      })
    );
  }
  /**
   * Retrieve total calls and chart for a specific organization using the Push API within the specified time range using either predefined time range options or custom from/to dates.
   * @param dateRange An object containing 'from' and 'to' date strings in ISO 8601 format.
   */
  getPushAPITotalRequests(t) {
    return this.api.get(
      this.buildPath(`${r.baseUrl}/pushapi/usage/total`, {
        from: t.from,
        to: t.to
      })
    );
  }
  /**
   * Retrieve total processed items and chart for a specific organization using the Push API within the specified time range using either predefined time range options or custom from/to dates.
   * @param dateRange An object containing 'from' and 'to' date strings in ISO 8601 format.
   */
  getPushAPITotalProcessedItems(t) {
    return this.api.get(
      this.buildPath(`${r.baseUrl}/pushapi/items/processed/total`, {
        from: t.from,
        to: t.to
      })
    );
  }
  /**
   * Retrieve Stream API throttling data for a specific organization within the specified time range.
   * @param dateRange An object containing 'from' and 'to' date strings in ISO 8601 format.
   */
  getStreamAPIThrottlingData(t) {
    return this.api.get(
      this.buildPath(`${r.baseUrl}/streamapi/usage/throttling`, {
        from: t.from,
        to: t.to
      })
    );
  }
  /**
   * Retrieve Push API throttling data for a specific organization within the specified time range.
   * @param dateRange An object containing 'from' and 'to' date strings in ISO 8601 format.
   */
  getPushAPIThrottlingData(t) {
    return this.api.get(
      this.buildPath(`${r.baseUrl}/pushapi/usage/throttling`, {
        from: t.from,
        to: t.to
      })
    );
  }
  /**
   * Retrieve aggregated IPE error metrics for a specific organization, including the overall totals and chart-friendly data, within the requested time range.
   * @param dateRange An object containing 'from' and 'to' date strings in ISO 8601 format.
   */
  getIPEMetricsTotal(t) {
    return this.api.get(
      this.buildPath(`${r.baseUrl}/performancemonitoring/ipe-metrics/total`, {
        from: t.from,
        to: t.to
      })
    );
  }
  /**
   * Retrieve IPE error metrics summarized over successive time intervals (e.g., hourly, daily) within the specified time range. The response provides a time-bucketed view (similar to a table or timeline) that helps analyze trends and patterns in error distribution.
   * @param dateRange An object containing 'from' and 'to' date strings in ISO 8601 format.
   */
  getIPEMetricsSummarize(t) {
    return this.api.get(
      this.buildPath(
        `${r.baseUrl}/performancemonitoring/ipe-metrics/summarize`,
        {
          from: t.from,
          to: t.to
        }
      )
    );
  }
  /**
   * Retrieve the average time in milliseconds for a document to become searchable after being crawled/pushed.
   *
   */
  getAverageIndexingProcessingTime() {
    return this.api.get(
      this.buildPath(
        `${r.baseUrl}/performancemonitoring/indexing/processing-time`
      )
    );
  }
  /**
   * Retrieve indexing processing time in milliseconds for a specific organization as a chart over the requested time range.
   *
   * @param {DateRangeParams} dateRange An object containing 'from' and 'to' date strings.
   * @returns {Promise<OrganizationHealthMonitoringIndexingProcessingTimeHistoryResponse>} Indexing processing time data for the specified time range.
   */
  getIndexingProcessingTimeHistory(t) {
    return this.api.get(
      this.buildPath(
        `${r.baseUrl}/performancemonitoring/indexing/processing-time/total`,
        {
          from: t.from,
          to: t.to
        }
      )
    );
  }
  /**
   * Retrieves total number of crawled items processed and chart for a specific organization within the specified time range using custom from/to dates.
   *
   * @param {DateRangeParams} dateRange An object containing 'from' and 'to' date strings.
   * @returns {Promise<OrganizationHealthMonitoringCrawlerTotalProcessedItemsResponse>} Total crawled items processed and chart data.
   */
  getCrawlerTotalProcessedItems(t) {
    return this.api.get(
      this.buildPath(`${r.baseUrl}/crawler/items/total`, {
        from: t.from,
        to: t.to
      })
    );
  }
  /**
   * Retrieve queries per second (QPS) time-series data for a specific organization within the specified time range, sourced from VictoriaMetrics.
   *
   * @param {DateRangeParams} dateRange An object containing 'from' and 'to' date strings.
   * @returns {Promise<OrganizationHealthMonitoringQPSResponse>} QPS data for the specified time range.
   */
  getQPSData(t) {
    return this.api.get(
      this.buildPath(`${r.baseUrl}/searchapi/usage/qps`, {
        from: t.from,
        to: t.to
      })
    );
  }
  /**
   * Retrieve total events and total events chart for a specific organization within the specified time range using custom from/to dates.
   *
   * @param {DateRangeParams} dateRange An object containing 'from' and 'to' date strings.
   * @returns {Promise<OrganizationHealthMonitoringTotalEventsResponse>} Total events and chart data.
   */
  getTotalEvents(t) {
    return this.api.get(
      this.buildPath(`${r.baseUrl}/events/total`, {
        from: t.from,
        to: t.to
      })
    );
  }
};
r.baseUrl = `/api/private/organizations/${h.orgPlaceholder}/monitoring`;
let i = r;
export {
  i as OrganizationHealthMonitoringResource
};
//# sourceMappingURL=OrganizationHealthMonitoringResource.js.map
